(function () {
	'use strict';

	const stylesheetPromises = new Map();
	const stylesheetTimeout = 10000;
	const generatedAttribute = 'data-syk-image-compare-generated';
	const ownedStylesheetAttribute = 'data-syk-image-compare-style';
	const stylesheetStateAttribute = 'data-syk-image-compare-style-state';

	if (!('customElements' in window) || window.customElements.get('syk-image-compare')) return;

	function normalizeUrl(value) {
		if (!value) return '';

		try {
			return new URL(value, document.baseURI).href;
		} catch (error) {
			return '';
		}
	}

	function findStylesheet(url) {
		const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');

		for (const stylesheet of stylesheets) {
			if (normalizeUrl(stylesheet.href) === url) return stylesheet;
		}

		return null;
	}

	function loadStylesheet(source) {
		const url = normalizeUrl(source);
		if (!url) return Promise.reject(new Error('Invalid image comparison stylesheet URL.'));
		if (stylesheetPromises.has(url)) return stylesheetPromises.get(url);

		let trackedPromise;
		const promise = new Promise(function (resolve, reject) {
			let stylesheet = findStylesheet(url);
			let timeoutId = 0;
			let settled = false;

			if (stylesheet && stylesheet.getAttribute(stylesheetStateAttribute) === 'error') {
				if (stylesheet.hasAttribute(ownedStylesheetAttribute)) stylesheet.remove();
				stylesheet = null;
			}

			if (stylesheet && stylesheet.sheet) {
				stylesheet.setAttribute(stylesheetStateAttribute, 'loaded');
				resolve(stylesheet);
				return;
			}

			if (!stylesheet) {
				stylesheet = document.createElement('link');
				stylesheet.rel = 'stylesheet';
				stylesheet.href = url;
				stylesheet.setAttribute(ownedStylesheetAttribute, '');
				stylesheet.setAttribute(stylesheetStateAttribute, 'loading');
				document.head.appendChild(stylesheet);
			}

			function cleanup() {
				stylesheet.removeEventListener('load', handleLoad);
				stylesheet.removeEventListener('error', handleError);
				if (timeoutId) window.clearTimeout(timeoutId);
			}

			function settleSuccess() {
				if (settled) return;
				settled = true;
				cleanup();
				stylesheet.setAttribute(stylesheetStateAttribute, 'loaded');
				resolve(stylesheet);
			}

			function settleFailure(message) {
				if (settled) return;
				settled = true;
				cleanup();
				stylesheet.setAttribute(stylesheetStateAttribute, 'error');
				if (stylesheet.hasAttribute(ownedStylesheetAttribute)) stylesheet.remove();
				reject(new Error(message));
			}

			function handleLoad() {
				settleSuccess();
			}

			function handleError() {
				settleFailure('Image comparison stylesheet failed to load.');
			}

			stylesheet.addEventListener('load', handleLoad, { once: true });
			stylesheet.addEventListener('error', handleError, { once: true });
			timeoutId = window.setTimeout(function () {
				settleFailure('Image comparison stylesheet load timed out.');
			}, stylesheetTimeout);
		});

		trackedPromise = promise.catch(function (error) {
			if (stylesheetPromises.get(url) === trackedPromise) stylesheetPromises.delete(url);
			throw error;
		});
		stylesheetPromises.set(url, trackedPromise);
		return trackedPromise;
	}

	function clamp(value, minimum, maximum) {
		return Math.min(Math.max(value, minimum), maximum);
	}

	function parseStart(value) {
		const parsed = parseFloat(value);
		return isFinite(parsed) ? clamp(parsed, 0, 100) : 50;
	}

	function getImage(source) {
		if (source.tagName === 'IMG') return source;

		const images = source.querySelectorAll('img');
		return images.length === 1 ? images[0] : null;
	}

	function getDirectSources(host) {
		const children = Array.from(host.children);
		if (children.length !== 2) return null;

		for (const child of children) {
			if (child.tagName !== 'PICTURE' && child.tagName !== 'IMG') return null;
			if (!getImage(child)) return null;
		}

		return children;
	}

	function roundValue(value) {
		return Math.round(value * 100) / 100;
	}

	class SykImageCompare extends HTMLElement {
		constructor() {
			super();
			this.connectionId = 0;
			this.mounted = false;
			this.activePointerId = null;
			this.position = 50;
			this.vertical = false;
			this.rtl = false;
			this.sources = null;
			this.sourceImages = null;
			this.baseSource = null;
			this.overlaySource = null;
			this.wrapper = null;
			this.control = null;
			this.generatedNodes = [];
			this.ownedClasses = [];
			this.originalPositionValue = '';
			this.originalPositionPriority = '';
			this.handlePointerDown = this.onPointerDown.bind(this);
			this.handlePointerMove = this.onPointerMove.bind(this);
			this.handlePointerEnd = this.onPointerEnd.bind(this);
			this.handleLostPointerCapture = this.onLostPointerCapture.bind(this);
			this.handleKeyDown = this.onKeyDown.bind(this);
			this.handleDragStart = this.onDragStart.bind(this);
		}

		connectedCallback() {
			if (this.mounted) return;

			const sources = getDirectSources(this);
			if (!sources) return;

			const styleSource = this.getAttribute('data-style-src');
			const connectionId = ++this.connectionId;

			loadStylesheet(styleSource).then(function () {
				if (!this.isConnected || connectionId !== this.connectionId || this.mounted) return;

				const currentSources = getDirectSources(this);
				if (!currentSources) return;

				try {
					this.mount(currentSources);
				} catch (error) {
					this.teardown();
				}
			}.bind(this)).catch(function () {
				// Keep the server-rendered first-image fallback intact.
			});
		}

		disconnectedCallback() {
			this.connectionId += 1;
			this.teardown();
		}

		addOwnedClass(element, className) {
			const existed = element.classList.contains(className);
			if (!existed) element.classList.add(className);
			this.ownedClasses.push({ element: element, className: className, existed: existed });
		}

		createGeneratedElement(tagName, className) {
			const element = document.createElement(tagName);
			if (className) element.className = className;
			element.setAttribute(generatedAttribute, '');
			this.generatedNodes.push(element);
			return element;
		}

		mount(sources) {
			const beforeSource = sources[0];
			const afterSource = sources[1];
			const beforeImage = getImage(beforeSource);
			const afterImage = getImage(afterSource);
			if (!beforeImage || !afterImage) return;

			this.sources = sources.slice();
			this.sourceImages = [beforeImage, afterImage];
			this.vertical = this.classList.contains('vertical');
			this.rtl = !this.vertical && String(document.documentElement.dir).toLowerCase() === 'rtl';

			const merchantStart = parseStart(this.getAttribute('data-start'));
			this.position = this.rtl ? 100 - merchantStart : merchantStart;
			this.baseSource = this.vertical || this.rtl ? afterSource : beforeSource;
			this.overlaySource = this.vertical || this.rtl ? beforeSource : afterSource;

			this.originalPositionValue = this.style.getPropertyValue('--syk-image-compare-position');
			this.originalPositionPriority = this.style.getPropertyPriority('--syk-image-compare-position');

			this.addOwnedClass(this, 'icv');
			this.addOwnedClass(this, this.vertical ? 'icv__icv--vertical' : 'icv__icv--horizontal');
			this.addOwnedClass(this, 'standard');
			this.addOwnedClass(beforeSource, 'icv__source');
			this.addOwnedClass(beforeSource, 'icv__source-before');
			this.addOwnedClass(afterSource, 'icv__source');
			this.addOwnedClass(afterSource, 'icv__source-after');
			this.addOwnedClass(this.baseSource, 'icv__source-base');
			this.addOwnedClass(this.overlaySource, 'icv__source-overlay');
			this.addOwnedClass(getImage(this.baseSource), 'icv__img');
			this.addOwnedClass(getImage(this.baseSource), 'icv__img-a');
			this.addOwnedClass(getImage(this.overlaySource), 'icv__img');
			this.addOwnedClass(getImage(this.overlaySource), 'icv__img-b');

			const beforeLabelAttribute = this.getAttribute('data-label-before');
			const afterLabelAttribute = this.getAttribute('data-label-after');
			const showLabels = beforeLabelAttribute !== null || afterLabelAttribute !== null;
			const beforeLabel = beforeLabelAttribute !== null ? beforeLabelAttribute : 'Before';
			const afterLabel = afterLabelAttribute !== null ? afterLabelAttribute : 'After';

			if (beforeLabelAttribute === '') this.addOwnedClass(this, 'no-label-before');
			if (afterLabelAttribute === '') this.addOwnedClass(this, 'no-label-after');

			this.appendChild(this.baseSource);

			this.wrapper = this.createGeneratedElement('div', 'icv__wrapper');
			this.wrapper.setAttribute('aria-hidden', 'false');
			this.wrapper.appendChild(this.overlaySource);
			this.appendChild(this.wrapper);

			const imposter = this.createGeneratedElement('div', 'icv__imposter');
			imposter.setAttribute('aria-hidden', 'true');
			this.appendChild(imposter);

			if (showLabels) {
				const beforeLabelElement = this.createGeneratedElement('span', 'icv__label icv__label-before');
				const afterLabelElement = this.createGeneratedElement('span', 'icv__label icv__label-after');
				beforeLabelElement.textContent = beforeLabel || 'Before';
				afterLabelElement.textContent = afterLabel || 'After';
				beforeLabelElement.setAttribute('aria-hidden', 'true');
				afterLabelElement.setAttribute('aria-hidden', 'true');
				if (this.vertical) {
					beforeLabelElement.classList.add('vertical');
					afterLabelElement.classList.add('vertical');
				}
				this.appendChild(beforeLabelElement);
				this.appendChild(afterLabelElement);
			}

			this.control = this.createControl(beforeLabel, afterLabel);
			this.appendChild(this.control);

			this.addEventListener('pointerdown', this.handlePointerDown);
			this.addEventListener('pointermove', this.handlePointerMove);
			this.addEventListener('pointerup', this.handlePointerEnd);
			this.addEventListener('pointercancel', this.handlePointerEnd);
			this.addEventListener('lostpointercapture', this.handleLostPointerCapture);
			this.addEventListener('dragstart', this.handleDragStart);
			this.control.addEventListener('keydown', this.handleKeyDown);

			this.mounted = true;
			this.applyPosition(this.position);
		}

		createControl(beforeLabel, afterLabel) {
			const control = this.createGeneratedElement('div', 'icv__control');
			control.tabIndex = 0;
			control.setAttribute('role', 'slider');
			control.setAttribute('aria-orientation', this.vertical ? 'vertical' : 'horizontal');
			control.setAttribute('aria-valuemin', '0');
			control.setAttribute('aria-valuemax', '100');
			control.setAttribute('aria-label', this.getControlLabel(beforeLabel, afterLabel));

			const firstLine = document.createElement('span');
			const secondLine = document.createElement('span');
			const themeWrapper = document.createElement('span');
			const firstArrow = document.createElement('span');
			const secondArrow = document.createElement('span');

			firstLine.className = 'icv__control-line';
			secondLine.className = 'icv__control-line';
			themeWrapper.className = 'icv__theme-wrapper';
			firstArrow.className = 'icv__arrow-wrapper';
			secondArrow.className = 'icv__arrow-wrapper';
			firstLine.setAttribute('aria-hidden', 'true');
			secondLine.setAttribute('aria-hidden', 'true');
			themeWrapper.setAttribute('aria-hidden', 'true');
			themeWrapper.appendChild(firstArrow);
			themeWrapper.appendChild(secondArrow);
			control.appendChild(firstLine);
			control.appendChild(themeWrapper);
			control.appendChild(secondLine);
			return control;
		}

		getControlLabel(beforeLabel, afterLabel) {
			const explicitLabel = String(this.getAttribute('data-control-label') || '').trim();
			if (explicitLabel) return explicitLabel;

			const labels = [beforeLabel, afterLabel].filter(function (label) {
				return String(label || '').trim();
			});
			if (labels.length) return labels.join(' / ');

			const alternativeTexts = this.sourceImages.map(function (image) {
				return String(image.getAttribute('alt') || '').trim();
			}).filter(Boolean);
			return alternativeTexts.length ? alternativeTexts.join(' / ') : 'Image comparison';
		}

		applyPosition(value) {
			this.position = clamp(value, 0, 100);
			this.style.setProperty('--syk-image-compare-position', this.position + '%');
			if (!this.control) return;

			const beforePercentage = this.vertical || !this.rtl ? this.position : 100 - this.position;
			const afterPercentage = 100 - beforePercentage;
			const beforeLabel = this.getAttribute('data-label-before') || 'Before';
			const afterLabel = this.getAttribute('data-label-after') || 'After';
			this.control.setAttribute('aria-valuenow', String(roundValue(this.position)));
			this.control.setAttribute('aria-valuetext', beforeLabel + ' ' + roundValue(beforePercentage) + '%, ' + afterLabel + ' ' + roundValue(afterPercentage) + '%');
		}

		positionFromPointer(event) {
			const bounds = this.getBoundingClientRect();
			if (!bounds.width || !bounds.height) return null;

			const value = this.vertical
				? (event.clientY - bounds.top) / bounds.height * 100
				: (event.clientX - bounds.left) / bounds.width * 100;
			return clamp(value, 0, 100);
		}

		onPointerDown(event) {
			if (!this.mounted || !event.isPrimary) return;
			if (event.pointerType === 'mouse' && event.button !== 0) return;

			const position = this.positionFromPointer(event);
			if (position === null) return;

			event.preventDefault();
			this.activePointerId = event.pointerId;
			try {
				this.setPointerCapture(event.pointerId);
			} catch (error) {}
			try {
				this.control.focus({ preventScroll: true });
			} catch (error) {
				this.control.focus();
			}
			this.applyPosition(position);
		}

		onPointerMove(event) {
			if (this.activePointerId !== event.pointerId) return;

			const position = this.positionFromPointer(event);
			if (position === null) return;
			event.preventDefault();
			this.applyPosition(position);
		}

		onPointerEnd(event) {
			if (this.activePointerId !== event.pointerId) return;

			this.activePointerId = null;
			try {
				if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
			} catch (error) {}
		}

		onLostPointerCapture(event) {
			if (this.activePointerId === event.pointerId) this.activePointerId = null;
		}

		onKeyDown(event) {
			if (event.altKey || event.ctrlKey || event.metaKey) return;

			const step = event.shiftKey ? 10 : 1;
			let nextPosition = null;

			switch (event.key) {
				case 'ArrowLeft':
					if (!this.vertical) nextPosition = this.position - step;
					break;
				case 'ArrowRight':
					if (!this.vertical) nextPosition = this.position + step;
					break;
				case 'ArrowUp':
					nextPosition = this.position - step;
					break;
				case 'ArrowDown':
					nextPosition = this.position + step;
					break;
				case 'PageUp':
					nextPosition = this.position - 10;
					break;
				case 'PageDown':
					nextPosition = this.position + 10;
					break;
				case 'Home':
					nextPosition = 0;
					break;
				case 'End':
					nextPosition = 100;
					break;
			}

			if (nextPosition === null) return;
			event.preventDefault();
			this.applyPosition(nextPosition);
		}

		onDragStart(event) {
			event.preventDefault();
		}

		teardown() {
			if (!this.mounted && !this.sources) return;

			this.removeEventListener('pointerdown', this.handlePointerDown);
			this.removeEventListener('pointermove', this.handlePointerMove);
			this.removeEventListener('pointerup', this.handlePointerEnd);
			this.removeEventListener('pointercancel', this.handlePointerEnd);
			this.removeEventListener('lostpointercapture', this.handleLostPointerCapture);
			this.removeEventListener('dragstart', this.handleDragStart);
			if (this.control) this.control.removeEventListener('keydown', this.handleKeyDown);

			if (this.activePointerId !== null) {
				try {
					if (this.hasPointerCapture(this.activePointerId)) this.releasePointerCapture(this.activePointerId);
				} catch (error) {}
			}
			this.activePointerId = null;

			for (const source of this.sources) this.appendChild(source);
			for (const generatedNode of this.generatedNodes) {
				if (generatedNode.parentNode) generatedNode.remove();
			}

			for (let index = this.ownedClasses.length - 1; index >= 0; index -= 1) {
				const record = this.ownedClasses[index];
				if (!record.existed) record.element.classList.remove(record.className);
			}

			if (this.originalPositionValue) {
				this.style.setProperty('--syk-image-compare-position', this.originalPositionValue, this.originalPositionPriority);
			} else {
				this.style.removeProperty('--syk-image-compare-position');
			}

			this.mounted = false;
			this.sources = null;
			this.sourceImages = null;
			this.baseSource = null;
			this.overlaySource = null;
			this.wrapper = null;
			this.control = null;
			this.generatedNodes = [];
			this.ownedClasses = [];
		}
	}

	window.customElements.define('syk-image-compare', SykImageCompare);
})();
